import React, { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isPast, isSunday, getDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SERVICES = [
  { id: '5min', name: '5 Minutes', price: 35, duration: 5 },
  { id: '15min', name: '15 Minutes', price: 50, duration: 15 },
  { id: '30min', name: '30 Minutes', price: 75, duration: 30 },
  { id: '45min', name: '45 Minutes', price: 100, duration: 45 },
  { id: '60min', name: '1 Hour', price: 135, duration: 60 },
  { id: 'consultation', name: 'Free Consultation', price: 0, duration: 30 },
];

// Generate time slots from 9 AM to 6 PM
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour < 18; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const time = `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
      slots.push(time);
    }
  }
  return slots;
};

const BUSINESS_ADDRESS = '1281 Parker Rd SE, Conyers, GA 30094';

const Booking = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [step, setStep] = useState(1);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enabledDates, setEnabledDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Auto-fill user info if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setCustomerName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
      setCustomerEmail(user.email || '');
      setCustomerPhone(user.phone || '');
    }
  }, [isAuthenticated, user]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Fetch enabled dates when month changes
  useEffect(() => {
    fetchEnabledDates();
  }, [currentMonth]);

  // Fetch time slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  const fetchEnabledDates = async () => {
    try {
      setLoadingDates(true);
      const startDate = format(monthStart, 'yyyy-MM-dd');
      const endDate = format(monthEnd, 'yyyy-MM-dd');

      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_URL}/specific-availability/dates/enabled?startDate=${startDate}&endDate=${endDate}`);
      const data = await response.json();

      if (data.success) {
        setEnabledDates(data.dates || []);
      }
    } catch (error) {
      console.error('Error fetching enabled dates:', error);
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoadingSlots(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');

      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${API_URL}/specific-availability/dates/${dateStr}/available-slots`);
      const data = await response.json();

      if (data.success && data.availableSlots) {
        // Convert 24-hour format to 12-hour format for display
        const slots = data.availableSlots.map(slot => {
          const [hours, minutes] = slot.split(':').map(Number);
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
          return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
        });
        setAvailableSlots(slots);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const generateTimeSlotsForDate = (startTime, endTime) => {
    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentHour = startHour;
    let currentMin = startMin;

    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const period = currentHour >= 12 ? 'PM' : 'AM';
      const displayHour = currentHour > 12 ? currentHour - 12 : currentHour === 0 ? 12 : currentHour;
      const time = `${displayHour}:${currentMin.toString().padStart(2, '0')} ${period}`;
      slots.push(time);

      // Increment by 30 minutes
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour += 1;
      }
    }

    return slots;
  };

  const isDateEnabled = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return enabledDates.some(d => d.date === dateStr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setMessage('');
    setMessageIsError(false);

    const convertTo24Hour = (time12h) => {
      const [time, period] = time12h.split(' ');
      let [hours, minutes] = time.split(':');
      hours = parseInt(hours);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
    };

    const showError = (msg) => {
      setMessage(msg);
      setMessageIsError(true);
    };

    try {
      if (isAuthenticated) {
        const bookingData = {
          appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
          startTime: convertTo24Hour(selectedTime),
          serviceName: selectedService.name,
          durationMinutes: selectedService.duration,
          price: selectedService.price,
          notes: customerNotes,
          phone: customerPhone
        };

        const response = await api.post('/appointments', bookingData);

        if (response.data.success) {
          setMessage('');
          setStep(6);
        } else {
          showError(response.data.error || 'Failed to create booking. Please try again.');
        }
      } else {
        // Guest booking
        const bookingData = {
          firstName: customerName.split(' ')[0] || customerName,
          lastName: customerName.split(' ').slice(1).join(' ') || '',
          email: customerEmail,
          phone: customerPhone,
          appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
          startTime: convertTo24Hour(selectedTime),
          serviceName: selectedService.name,
          durationMinutes: selectedService.duration,
          price: selectedService.price,
          notes: customerNotes
        };

        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_URL}/appointments/guest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bookingData),
        });

        const data = await response.json();

        if (response.ok) {
          setMessage('');
          setStep(6);
        } else {
          showError(data.error || 'Failed to create booking. Please try again.');
        }
      }
    } catch (error) {
      console.error('Booking error:', error);
      const serverMsg = error.response?.data?.error;
      if (serverMsg === 'Time slot is no longer available') {
        showError('Sorry — that time was just taken. Please go back and choose a different time.');
      } else if (serverMsg) {
        showError(serverMsg);
      } else {
        showError('Something went wrong on our end. Please try again or call us at (678) 938-6638.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-secondary py-20">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-4xl font-light text-primary mb-12 text-center">
          Book Your Appointment
        </h1>
        {message && (
          <div className={`mb-6 px-4 py-3 rounded ${messageIsError ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-green-100 text-green-700'}`}>
            {message}
          </div>
        )}
        <div className="bg-white rounded-2xl shadow-lg p-8">

          {/* Step 1: Select Service */}
          {step === 1 && (
            <>
              <h2 className="text-2xl text-primary mb-6">Step 1: Select Service</h2>
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {SERVICES.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => setSelectedService(s)}
                    className={'cursor-pointer border-2 rounded-lg p-4 ' + (selectedService?.id === s.id ? 'border-primary bg-tertiary' : 'border-secondary')}
                  >
                    <h3 className="text-lg text-primary">{s.name}</h3>
                    <p className="text-2xl text-primary">{s.price === 0 ? 'Free' : '$' + s.price}</p>
                    <p className="text-sm text-primary mt-2">{s.duration} minutes</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!selectedService}
                className="w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Date Selection
              </button>
            </>
          )}

          {/* Step 2: Select Date */}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="text-primary mb-4 hover:underline">&larr; Back to Service Selection</button>
              <h2 className="text-2xl text-primary mb-6">Step 2: Select Date</h2>
              {loadingDates && (
                <div className="text-center text-primary mb-4">Loading available dates...</div>
              )}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="text-primary hover:text-secondary">
                    &larr; Previous
                  </button>
                  <h3 className="text-xl text-primary">{format(currentMonth, 'MMMM yyyy')}</h3>
                  <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="text-primary hover:text-secondary">
                    Next &rarr;
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-primary py-2">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: getDay(monthStart) }).map((_, i) => (
                    <div key={`empty-${i}`}></div>
                  ))}
                  {daysInMonth.map((day, idx) => {
                    const isPastDate = isPast(day) && !isToday(day);
                    const isEnabled = isDateEnabled(day);
                    const isDisabled = isPastDate || !isEnabled;
                    const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');

                    return (
                      <button
                        key={idx}
                        onClick={() => !isDisabled && setSelectedDate(day)}
                        disabled={isDisabled}
                        className={`p-3 rounded-lg text-center ${
                          isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-30' :
                          isSelected ? 'bg-primary text-white' :
                          isToday(day) ? 'bg-secondary text-primary font-bold' :
                          'bg-white text-primary hover:bg-tertiary'
                        } border-2 border-secondary`}
                      >
                        {format(day, 'd')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                onClick={() => setStep(3)}
                disabled={!selectedDate}
                className="w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Time Selection
              </button>
            </>
          )}

          {/* Step 3: Select Time */}
          {step === 3 && (
            <>
              <button onClick={() => setStep(2)} className="text-primary mb-4 hover:underline">&larr; Back to Date Selection</button>
              <h2 className="text-2xl text-primary mb-6">Step 3: Select Time</h2>
              <p className="text-primary mb-6">Available times for <span className="font-medium">{selectedDate && format(selectedDate, 'MMMM d, yyyy')}</span></p>
              {loadingSlots && (
                <div className="text-center text-primary mb-4">Loading available times...</div>
              )}
              {!loadingSlots && availableSlots.length === 0 && (
                <div className="text-center text-primary mb-4 p-4 bg-yellow-50 rounded-lg">
                  No available time slots for this date. Please select another date.
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
                {availableSlots.map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setSelectedTime(slot)}
                    className={`p-3 rounded-lg border-2 ${
                      selectedTime === slot ? 'border-primary bg-primary text-white' : 'border-secondary bg-white text-primary'
                    } hover:bg-tertiary transition-colors`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(4)}
                disabled={!selectedTime}
                className="w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Contact Info
              </button>
            </>
          )}

          {/* Step 4: Contact Information */}
          {step === 4 && (
            <>
              <button onClick={() => setStep(3)} className="text-primary mb-4 hover:underline">&larr; Back to Time Selection</button>
              <h2 className="text-2xl text-primary mb-6">Step 4: Your Information</h2>

              {isAuthenticated && (
                <div className="bg-accent-light/20 border-l-4 border-accent rounded-lg p-4 mb-6">
                  <p className="text-primary font-medium">✓ Logged in as {user?.firstName}</p>
                  <p className="text-sm text-primary-light">Your information has been auto-filled from your account.</p>
                </div>
              )}

              <div className="bg-tertiary rounded-lg p-6 mb-6">
                <h3 className="font-medium text-primary mb-3">Booking Summary</h3>
                <div className="space-y-2 text-primary">
                  <p><span className="font-medium">Service:</span> {selectedService.name}</p>
                  <p><span className="font-medium">Date:</span> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}</p>
                  <p><span className="font-medium">Time:</span> {selectedTime}</p>
                  <p><span className="font-medium">Price:</span> {selectedService.price === 0 ? 'Free' : `$${selectedService.price}`}</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-primary mb-2">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={isAuthenticated}
                    className={`w-full px-4 py-3 border border-secondary rounded-md focus:outline-none focus:border-primary ${isAuthenticated ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-primary mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    disabled={isAuthenticated}
                    className={`w-full px-4 py-3 border border-secondary rounded-md focus:outline-none focus:border-primary ${isAuthenticated ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-primary mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-secondary rounded-md focus:outline-none focus:border-primary"
                    placeholder="(678) 555-1234"
                  />
                </div>
                <div>
                  <label className="block text-primary mb-2">Additional Notes (optional)</label>
                  <textarea
                    rows="3"
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    className="w-full px-4 py-3 border border-secondary rounded-md focus:outline-none focus:border-primary"
                    placeholder="Any special requests or information..."
                  />
                </div>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (!customerEmail || !customerEmail.includes('@')) {
                      alert('Please enter a valid email address');
                      return;
                    }
                    if (!customerPhone || customerPhone.trim().length < 7) {
                      alert('Please enter a valid phone number');
                      return;
                    }
                    if (isAuthenticated) {
                      handleSubmit(new Event('submit'));
                    } else {
                      setStep(5);
                    }
                  }}
                  className="w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Booking...' : isAuthenticated ? 'Confirm Booking' : 'Continue to Review'}
                </button>
              </form>
            </>
          )}

          {/* Step 5: Login or Continue as Guest */}
          {step === 5 && (
            <>
              <button onClick={() => setStep(4)} className="text-primary mb-4 hover:underline">&larr; Back to Contact Info</button>
              <h2 className="text-2xl text-primary mb-6">Step 5: Review & Confirm</h2>

              <div className="bg-tertiary rounded-lg p-6 mb-6">
                <h3 className="font-medium text-primary mb-3">Booking Summary</h3>
                <div className="space-y-2 text-primary">
                  <p><span className="font-medium">Service:</span> {selectedService.name}</p>
                  <p><span className="font-medium">Date:</span> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}</p>
                  <p><span className="font-medium">Time:</span> {selectedTime}</p>
                  <p><span className="font-medium">Price:</span> {selectedService.price === 0 ? 'Free' : `$${selectedService.price}`}</p>
                  <p><span className="font-medium">Name:</span> {customerName}</p>
                  <p><span className="font-medium">Email:</span> {customerEmail}</p>
                  <p><span className="font-medium">Phone:</span> {customerPhone}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-2 border-secondary rounded-lg p-6">
                  <h3 className="text-lg font-medium text-primary mb-3">Have an account?</h3>
                  <p className="text-primary mb-4">Login to manage your appointments and view booking history.</p>
                  <a
                    href="/login"
                    className="block w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors text-center"
                  >
                    Login to Your Account
                  </a>
                </div>

                <div className="text-center text-primary">or</div>

                <div className="border-2 border-secondary rounded-lg p-6">
                  <h3 className="text-lg font-medium text-primary mb-3">Continue as Guest</h3>
                  <p className="text-primary mb-4">Complete your booking without creating an account.</p>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="block w-full bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Booking...' : 'Confirm Booking as Guest'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Step 6: Confirmation */}
          {step === 6 && (
            <div className="text-center">
              <div className="mb-6">
                <svg className="w-20 h-20 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <h2 className="text-3xl font-light text-primary mb-4">Booking Confirmed!</h2>
                <p className="text-lg text-primary mb-6">
                  Thank you for booking with Key Electrolysis Services. <br/>
                  A confirmation email has been sent to <span className="font-medium">{customerEmail}</span>
                </p>
              </div>

              <div className="bg-tertiary rounded-lg p-6 mb-6 max-w-md mx-auto">
                <h3 className="font-medium text-primary mb-4">Appointment Details</h3>
                <div className="space-y-2 text-primary text-left">
                  <p><span className="font-medium">Service:</span> {selectedService.name}</p>
                  <p><span className="font-medium">Date:</span> {selectedDate && format(selectedDate, 'MMMM d, yyyy')}</p>
                  <p><span className="font-medium">Time:</span> {selectedTime}</p>
                  <p><span className="font-medium">Price:</span> {selectedService.price === 0 ? 'Free' : `$${selectedService.price}`}</p>
                  <p><span className="font-medium">Location:</span> {BUSINESS_ADDRESS}</p>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href="https://keyelectrolysis.com"
                  className="block bg-primary text-white px-6 py-3 rounded-md hover:bg-secondary hover:text-primary transition-colors"
                >
                  Return to Home
                </a>
                <button
                  onClick={() => {
                    setStep(1);
                    setSelectedService(null);
                    setSelectedDate(null);
                    setSelectedTime(null);
                    setCustomerName('');
                    setCustomerEmail('');
                    setCustomerPhone('');
                    setCustomerNotes('');
                  }}
                  className="block w-full bg-white border-2 border-primary text-primary px-6 py-3 rounded-md hover:bg-tertiary transition-colors"
                >
                  Book Another Appointment
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;
